import { Button, type ButtonProps } from "@chakra-ui/react";
import { motion as framerMotion } from "framer-motion";
import type { ReactNode } from "react";

interface GlowButtonProps extends ButtonProps {
  readonly children: ReactNode;
  readonly glow?: "pink" | "blue" | "yellow";
  readonly variantKind?: "primary" | "secondary" | "ghost";
  readonly motion?: "glowPulse" | "lift" | "none";
  readonly href?: string;
  readonly target?: string;
  readonly rel?: string;
}

const glowMap = {
  pink: {
    borderGradient:
      "linear-gradient(90deg, #37d5ff 0%, #5b7dff 26%, #ff4fd8 68%, #ff65e5 100%)",
    shadow:
      "0 0 22px rgba(255,79,216,0.44), 0 0 34px rgba(55,213,255,0.18)"
  },
  blue: {
    borderGradient:
      "linear-gradient(90deg, #37d5ff 0%, #5b7dff 54%, #ff4fd8 100%)",
    shadow:
      "0 0 22px rgba(55,213,255,0.38), 0 0 30px rgba(255,79,216,0.12)"
  },
  yellow: {
    borderGradient:
      "linear-gradient(90deg, #ffef68 0%, #ffb300 52%, #ff4fd8 100%)",
    shadow:
      "0 0 22px rgba(255,216,77,0.36), 0 0 30px rgba(255,79,216,0.14)"
  }
};

export function GlowButton({
  children,
  glow = "pink",
  variantKind = "primary",
  motion = "glowPulse",
  ...props
}: GlowButtonProps) {
  const colors = glowMap[glow];
  const isGhost = variantKind === "ghost";
  const isSecondary = variantKind === "secondary";
  const hoverMotion =
    motion === "none" ? undefined : motion === "lift" ? { y: -2 } : { scale: 1.035 };

  return (
    <framerMotion.span
      whileHover={hoverMotion}
      whileTap={motion === "none" ? undefined : { scale: 0.97 }}
      style={{ display: "inline-flex", borderRadius: 999 }}
    >
      <Button
        minH="50px"
        px={{ base: 5, md: 7 }}
        border="2px solid transparent"
        borderRadius="999px"
        bg={
          isGhost
            ? "transparent"
            : isSecondary
              ? "rgba(3,5,17,0.46)"
              : `linear-gradient(rgba(6,7,20,0.72), rgba(6,7,20,0.72)) padding-box, ${colors.borderGradient} border-box`
        }
        borderColor={isSecondary ? "rgba(255,255,255,0.18)" : "transparent"}
        color="white"
        boxShadow={
          isGhost
            ? "none"
            : isSecondary
              ? "0 0 20px rgba(255,255,255,0.06), inset 0 0 0 1px rgba(255,255,255,0.04)"
              : colors.shadow
        }
        fontWeight={400}
        textTransform="uppercase"
        position="relative"
        overflow="hidden"
        _hover={{
          bg: isGhost
            ? "transparent"
            : isSecondary
              ? "rgba(8,10,28,0.68)"
              : `linear-gradient(rgba(8,9,26,0.82), rgba(8,9,26,0.82)) padding-box, ${colors.borderGradient} border-box`,
          borderColor: isSecondary ? "rgba(255,255,255,0.28)" : "transparent",
          filter: "brightness(1.12)",
          boxShadow: isGhost
            ? colors.shadow
            : isSecondary
              ? "0 0 24px rgba(55,213,255,0.16), inset 0 0 0 1px rgba(255,255,255,0.08)"
              : `${colors.shadow}, 0 0 34px ${glow === "blue" ? "rgba(55,213,255,0.36)" : glow === "yellow" ? "rgba(255,216,77,0.36)" : "rgba(255,79,216,0.42)"}`
        }}
        _active={{ filter: "brightness(0.94)" }}
        {...props}
      >
        {children}
      </Button>
    </framerMotion.span>
  );
}
